import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Check, Star, ArrowRight, Crown } from 'lucide-react';

export default function VendorPricingPage() {
  const navigate = useNavigate();
  
  const plans = [
    {
      name: 'Standard Monthly',
      price: '£10',
      period: '/month',
      description: 'Perfect for getting started',
      features: [
        'Business listing in directory',
        'Up to 10 gallery images',
        'Contact form for enquiries',
        'Business description & details',
        'Social media links',
        'Customer reviews'
      ],
      cta: 'Get Started',
      popular: false
    },
    {
      name: 'Annual',
      price: '£100',
      period: '/year',
      description: '2 months FREE!',
      features: [
        'Everything in Standard',
        'Save £20 per year',
        'Priority support',
        'Up to 10 gallery images',
        'Promotional video (30 sec)',
        'All standard features included'
      ],
      cta: 'Best Value',
      popular: true,
      savings: 'Save £20'
    }
  ];

  const featuredPlans = [
    {
      name: 'Featured Monthly',
      price: '£20',
      period: '/month',
      description: 'Maximum visibility',
      features: [
        'Everything in Standard',
        'Featured badge on listing',
        'Homepage featured section',
        'Priority search placement',
        'Highlighted in category',
        'Premium visibility'
      ],
      cta: 'Go Featured',
      featured: true
    },
    {
      name: 'Featured Annual',
      price: '£200',
      period: '/year',
      description: '2 months FREE!',
      features: [
        'Everything in Featured Monthly',
        'Save £40 per year',
        'Maximum exposure all year',
        'Priority support',
        'Best value for featured',
        'All premium features'
      ],
      cta: 'Best Featured Value',
      featured: true,
      savings: 'Save £40'
    }
  ];

  return (
    <div className="min-h-screen py-12" data-testid="vendor-pricing-page">
      <div className="container-wedding">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="caption mb-3">Join Gibraltar Wedding Expo</p>
          <h1 className="heading-xl mb-4">Vendor Pricing</h1>
          <p className="text-stone-600 max-w-2xl mx-auto text-lg">
            Reach couples planning their dream Mediterranean wedding in Gibraltar. 
            Choose the plan that works best for your business.
          </p>
        </div>

        {/* Standard Plans */}
        <div className="mb-12">
          <h2 className="text-center text-xl font-semibold mb-6">Standard Listings</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {plans.map((plan) => (
              <div 
                key={plan.name}
                className={`bg-white rounded-sm border-2 p-8 relative ${
                  plan.popular 
                    ? 'border-[#5C0120] shadow-lg' 
                    : 'border-stone-200'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-[#5C0120] text-white text-xs font-semibold px-4 py-1 rounded-full">
                      MOST POPULAR
                    </span>
                  </div>
                )}
                {plan.savings && (
                  <div className="absolute top-4 right-4">
                    <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-1 rounded">
                      {plan.savings}
                    </span>
                  </div>
                )}
                
                <h3 className="text-xl font-semibold mb-2">{plan.name}</h3>
                <p className="text-stone-500 text-sm mb-4">{plan.description}</p>
                
                <div className="mb-6">
                  <span className="text-4xl font-bold">{plan.price}</span>
                  <span className="text-stone-500">{plan.period}</span>
                </div>
                
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-stone-600 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Button 
                  onClick={() => navigate('/vendor/auth?mode=register&plan=' + encodeURIComponent(plan.name))}
                  className={`w-full ${plan.popular ? 'btn-primary' : 'bg-stone-800 hover:bg-stone-700 text-white'}`}
                >
                  {plan.cta}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Featured Plans */}
        <div className="mb-12">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 bg-[#D4AF37]/10 text-[#D4AF37] px-4 py-2 rounded-full mb-4">
              <Crown className="h-5 w-5" />
              <span className="font-semibold">Featured Listings</span>
            </div>
            <h2 className="text-xl font-semibold">Get Maximum Visibility</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {featuredPlans.map((plan) => (
              <div 
                key={plan.name}
                className="bg-gradient-to-b from-[#D4AF37]/5 to-white rounded-sm border-2 border-[#D4AF37] p-8 relative"
              >
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-[#D4AF37] text-white text-xs font-semibold px-4 py-1 rounded-full flex items-center gap-1">
                    <Star className="h-3 w-3 fill-current" /> FEATURED
                  </span>
                </div>
                {plan.savings && (
                  <div className="absolute top-4 right-4">
                    <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-1 rounded">
                      {plan.savings}
                    </span>
                  </div>
                )}
                
                <h3 className="text-xl font-semibold mb-2">{plan.name}</h3>
                <p className="text-stone-500 text-sm mb-4">{plan.description}</p>
                
                <div className="mb-6">
                  <span className="text-4xl font-bold text-[#D4AF37]">{plan.price}</span>
                  <span className="text-stone-500">{plan.period}</span>
                </div>
                
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                      <span className="text-stone-600 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Button 
                  onClick={() => navigate('/vendor/auth?mode=register&plan=' + encodeURIComponent(plan.name))}
                  className="w-full bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white"
                >
                  {plan.cta}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* How it works */}
        <div className="bg-stone-50 rounded-sm p-8 max-w-4xl mx-auto">
          <h3 className="text-xl font-semibold text-center mb-6">How It Works</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-[#5C0120] text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                1
              </div>
              <h4 className="font-semibold mb-2">Apply</h4>
              <p className="text-stone-600 text-sm">
                Submit your business application with your details and preferred plan.
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-[#5C0120] text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                2
              </div>
              <h4 className="font-semibold mb-2">Approval</h4>
              <p className="text-stone-600 text-sm">
                We review your application and contact you with payment details.
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-[#5C0120] text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                3
              </div>
              <h4 className="font-semibold mb-2">Go Live</h4>
              <p className="text-stone-600 text-sm">
                Once payment is confirmed, your listing goes live immediately.
              </p>
            </div>
          </div>
          <div className="mt-6 p-4 bg-white rounded-sm border border-stone-200 text-center">
            <p className="text-sm text-stone-600">
              <strong>Payment Options:</strong> Annual payments or monthly standing order. 
              Payment details will be sent after your application is approved.
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <p className="text-stone-600 mb-4">Already have an account?</p>
          <Link to="/vendor/auth">
            <Button variant="outline">
              Sign In to Your Dashboard
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
